import {
  type AuthenticationResult,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { msalConfig } from '@/auth/authConfig.ts';

export const msalInstance = new PublicClientApplication(msalConfig);

const BASE_SCOPES = ['openid', 'profile'];
export const API_SCOPES = [import.meta.env.VITE_BACKEND_API_SCOPE as string];

export async function initMsal(): Promise<void> {
  await msalInstance.initialize();

  if (!msalInstance.getActiveAccount()) {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const result = event.payload as AuthenticationResult;
      if (result.account) {
        msalInstance.setActiveAccount(result.account);
      }
    }
  });
}

export function isInteractionRequired(err: unknown): boolean {
  const code = (err as any)?.errorCode ?? (err as any)?.subError ?? (err as any)?.message ?? '';
  return (
    typeof code === 'string' &&
    /interaction_required|login_required|consent_required|no_tokens_found/i.test(code)
  );
}

export async function acquireAccessTokenSilentOrThrow(
  scopes: string[] = API_SCOPES
): Promise<string> {
  let account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;

  if (!account) {
    const res = await msalInstance.loginPopup({
      scopes: [...BASE_SCOPES, ...scopes],
      prompt: 'consent',
    });
    msalInstance.setActiveAccount(res.account!);
    account = res.account!;
  }

  try {
    const res = await msalInstance.acquireTokenSilent({ account, scopes });
    return res.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError || isInteractionRequired(e)) {
      const i = await msalInstance.acquireTokenPopup({
        account,
        scopes,
        prompt: 'consent',
      });
      return i.accessToken;
    }
    throw e;
  }
}
